import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import usePupilLearningDraft from "./usePupilLearningDraft";
import LessonPreparation from "./LessonPreparation";
import SupportPercentChoice from "./SupportPercentChoice";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function PupilLearningQueue({ refreshKey = 0, onActivate }) {
  const [queue, setQueue] = useState(null);
  const [open, setOpen] = useState(false);
  const draft = usePupilLearningDraft(queue?.next_item);
  const { text, outcome, support_received_percent: supportReceived } = draft.values;
  const setText = value => draft.edit({ text: value });
  const setOutcome = value => draft.edit({ outcome: value });
  const setSupportReceived = value => draft.edit({ support_received_percent: value });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const generation = useRef(0);
  const readSequence = useRef(0);
  const locked = useRef(false);

  const load = useCallback(async (version) => {
    const request = ++readSequence.current;
    try {
      const { data } = await axios.get(`${API}/api/learner-framework/me/learning-queue`, { withCredentials: true });
      if (version === generation.current && request === readSequence.current) {
        setQueue(data); setError("");
      }
    } catch (err) {
      if (version === generation.current && request === readSequence.current) {
        setError(err?.response?.status === 404
          ? "Your learning questions will appear here when your pupil account is connected."
          : "Your learning to-do list could not load. Try again when you are ready.");
      }
    }
  }, []);

  useEffect(() => {
    const version = ++generation.current;
    load(version);
    return () => { generation.current += 1; };
  }, [load, refreshKey]);

  const submit = async (event) => {
    event.preventDefault();
    const item = queue?.next_item;
    const homework = item?.interaction === "homework_response";
    const reflection = item?.interaction === "lesson_reflection";
    const followUp = item?.interaction === "review_follow_up";
    const supportStep = reflection && item.question_step === 0;
    if (!item || (!supportStep && (homework ? !outcome : !text.trim())) || locked.current) return;
    locked.current = true;
    setBusy(true);
    setError("");
    const version = generation.current;
    try {
      const evidenceQuestion = item.interaction === "evidence_question";
      const endpoint = evidenceQuestion ? `/learner-framework/me/learning-questions/${encodeURIComponent(item.check_id)}/answer` : followUp
        ? (item.response_source_kind === "reflection"
          ? `/after-lesson-reflection/lessons/${encodeURIComponent(item.response_source_id)}/follow-up`
          : `/learner-framework/me/learning-queue/${item.response_source_kind}/${encodeURIComponent(item.response_source_id)}/follow-up`)
        : reflection
        ? `/after-lesson-reflection/${supportStep ? `lessons/${encodeURIComponent(item.lesson_id)}/support` : "respond"}`
        : `/learner-framework/me${homework ? `/homework/${encodeURIComponent(item.assignment_id)}/complete` : "/learning-turn"}`;
      const payload = evidenceQuestion ? { source_revision: item.review_source_revision, answer: text.trim() } : followUp ? { task_id: item.follow_up_task_id, source_revision: item.review_source_revision, answer: text.trim() } : reflection ? (supportStep ? { support_received_percent: supportReceived } : {
        signal_key: item.signal_key, trigger: "lesson_just_completed", lesson_id: item.lesson_id,
        learner_text: text.trim(), support_received_percent: item.support_received_percent ?? null,
      }) : homework ? { outcome, note: text.trim() || null } : {
        message: text.trim(), check_id: item.check_id, question_step: item.question_step, focus: item.focus,
      };
      const { data } = await axios.post(`${API}/api${endpoint}`, payload, { withCredentials: true });
      if (evidenceQuestion && (data.ok !== true || data.task_id !== item.check_id || !data.answered_at)) throw new Error("Answer save not confirmed");
      if (followUp && (data.ok !== true || data.task_id !== item.follow_up_task_id || !data.answered_at)) throw new Error("Follow-up save not confirmed");
      if (reflection && (data.ok !== true || data.lesson_id !== item.lesson_id || (supportStep ? !data.support_answered_at : !data.responded_at))) throw new Error("Reflection save not confirmed");
      if (version !== generation.current) return;
      draft.retire();
      setFeedback(supportStep ? "Your support response is saved." : data.feedback || data.follow_up?.body || "Your answer is saved for instructor review.");
      // Clear the answered question before reload. Failed reads cannot invite
      // another answer to the same item or falsely claim the list is complete.
      setQueue(null);
      await load(version);
    } catch (err) {
      if (version === generation.current) setError(err?.response?.status === 409
        ? "An answer is already saved. Your words are still here. Reload to see the current question."
        : "Your answer could not be confirmed as saved. Your words are still here; you can retry.");
    } finally {
      locked.current = false;
      if (version === generation.current) setBusy(false);
    }
  };

  const leaveReflection = async () => {
    const current = queue?.next_item;
    if (current?.interaction !== "lesson_reflection" || locked.current) return;
    locked.current = true; setBusy(true); setError("");
    const version = generation.current;
    try {
      const { data } = await axios.post(`${API}/api/after-lesson-reflection/dismiss`, { signal_key: current.signal_key, trigger: "lesson_just_completed" }, { withCredentials: true });
      if (data.ok !== true) throw new Error("Dismissal unconfirmed");
      if (version !== generation.current) return;
      draft.retire();
      setFeedback("This reflection has been left aside. It has not been marked as learning progress.");
      setQueue(null); await load(version);
    } catch {
      if (version === generation.current) setError("I couldn’t confirm that change. Your reflection is still here.");
    } finally {
      locked.current = false;
      if (version === generation.current) setBusy(false);
    }
  };

  const preparationSaved = () => {
    draft.retire();
    setFeedback("Your lesson choices are saved.");
    setBusy(false);
    setQueue(null);
    load(generation.current);
  };

  const item = queue?.next_item;
  const reflection = item?.interaction === "lesson_reflection";
  const supportStep = reflection && item.question_step === 0;
  return <section data-testid="pupil-learning-queue" className="border-t border-zinc-700 pt-4 text-left">
    <button type="button" className="min-h-11 underline text-emerald-300" onClick={async () => {
      if (open && !(await draft.flush())) return;
      if (!open) { onActivate?.(); load(generation.current); }
      setOpen(!open);
    }} aria-expanded={open} disabled={busy}>
      {open ? "Pause my learning to-do list" : "My learning to-do list"}
    </button>
    {open && <div className="space-y-3 mt-3">
      <p className="text-sm text-zinc-300">One learning task at a time. Your list brings together understanding questions, assigned preparation, optional reflection and choices for your next lesson.</p>
      {draft.status && <p role="status">{draft.status === "saved" ? "Private draft saved — not submitted." : draft.status === "saving" ? "Saving private draft…" : "Your draft could not be confirmed as saved. Keep this page open to keep your words."}</p>}
      {draft.status === "error" && <button type="button" className="min-h-11 underline" onClick={() => draft.flush()}>Retry draft save</button>}
      {feedback && <p role="status" className="text-sm">{feedback}</p>}
      {error && <div role="alert"><p>{error}</p><button type="button" className="min-h-11 underline" disabled={busy} onClick={() => load(generation.current)}>Reload my questions</button></div>}
      {!queue && !error && <p role="status">Loading your questions…</p>}
      {queue?.counts_complete === false && <p role="status">Some learning source records could not be included. The counts cover the work available here; more may be available in your lesson records.</p>}
      {item?.interaction === "lesson_preparation" && <div className="space-y-3">
        <p>{item.focus} · {item.reason}</p>
        <LessonPreparation key={item.draft?.token || item.id} initialDraft={draft.values.preparation} onDraftChange={item.draft ? preparation => draft.edit({ preparation }) : undefined} lessonId={item.lesson_id} initialOpen onSaved={preparationSaved} onBusyChange={setBusy} />
        <p className="text-sm text-zinc-400">{item.evidence_required}</p>
      </div>}
      {item && item.interaction !== "lesson_preparation" && <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-zinc-400">{item.focus} · {item.reason}</p>
        {item.answer && <p className="text-sm leading-relaxed">{item.answer}</p>}
        {(item.instructions || []).map((line, index) => <p key={index} className="leading-relaxed">{line}</p>)}
        {!!item.review_prompts?.length && <div><p>Questions to think about</p><ul className="list-disc pl-4">{item.review_prompts.map((prompt, index) => <li key={index}>{prompt}</li>)}</ul></div>}
        {!!item.evidence_limitations?.length && <div><p>About this preparation</p><ul className="list-disc pl-4">{item.evidence_limitations.map((limitation, index) => <li key={index}>{limitation}</li>)}</ul></div>}
        {!!item.success_indicators?.length && <p className="text-sm">Evidence to discuss: {item.success_indicators.join(" · ")}</p>}
        {item.content_lesson_id && <a className="block min-h-11 underline" href={`/lesson/${encodeURIComponent(item.content_lesson_id)}`} target="_blank" rel="noopener noreferrer">Open the assigned learning material</a>}
        {item.interaction === "homework_response" ? <fieldset disabled={busy} className="space-y-2">
          <legend className="text-lg">{item.check_question}</legend>
          {[["understood", "I understand it"], ["practised", "I tried it"], ["need_more_help", "I need more help"], ["not_confident", "I’m still unsure"]].map(([value, label]) =>
            <button key={value} type="button" className="block min-h-11 underline" aria-pressed={outcome === value} onClick={() => setOutcome(value)}>{label}{outcome === value ? " — selected" : ""}</button>)}
        </fieldset> : null}
        {supportStep ? <>
          <SupportPercentChoice label={item.check_question} value={supportReceived} onChange={setSupportReceived} allowNone disabled={busy} />
          {item.support_requested_percent != null && <p>You requested {item.support_requested_percent}% support beforehand. Your answer can be different.</p>}
        </> : <>
        <label htmlFor="pupil-queue-answer" className="block text-lg">{item.interaction === "homework_response" ? "Your own words (optional)" : item.check_question}</label>
        <textarea id="pupil-queue-answer" value={text} onChange={event => setText(event.target.value)} maxLength={item.interaction === "homework_response" ? 500 : reflection ? 1000 : 2000}
          disabled={busy} rows={3} className="w-full rounded border border-zinc-600 bg-zinc-900 p-3 text-base"
          placeholder="Your words are enough. ‘I’m not sure’ is an answer too." />
        </>}
        <button type="submit" disabled={busy || (!supportStep && (item.interaction === "homework_response" ? !outcome : !text.trim()))} className="min-h-11 underline">{busy ? "Saving…" : supportStep ? "Save and continue" : "Save my answer"}</button>
        {reflection && <button type="button" className="block min-h-11 underline" disabled={busy} onClick={leaveReflection}>Leave this reflection</button>}
        <p className="text-sm text-zinc-400">{item.evidence_required}</p>
      </form>}
      {queue && !item && !error && <p>No tasks from the connected learning sources are waiting for your answer here.</p>}
      {!!queue?.awaiting_review_count && <p className="text-sm text-zinc-400">{queue.awaiting_review_count} learning {queue.awaiting_review_count === 1 ? "item is" : "items are"} awaiting instructor review. This does not confirm practical progress.</p>}
      {!!queue?.reviewed_count && <p className="text-sm text-zinc-400">Your instructor has closed {queue.reviewed_count} response {queue.reviewed_count === 1 ? "review" : "reviews"}. This is separate from practical progress.</p>}
    </div>}
  </section>;
}


